import { useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { apiPost } from "../lib/api";

const COLORS = {
  bg: "#F8F7F4",
  surface: "#FFFFFF",
  text: "#1A1917",
  textSecondary: "#6B6860",
  textMuted: "#9C9890",
  accent: "#2B5CE6",
  accentLight: "#EEF2FF",
  border: "#E8E6E1",
  green: "#1A8754",
  greenLight: "#ECFDF5",
};

const TIERS = [
  {
    name: "Free",
    price: "0",
    period: "",
    description: "Try Relay with limited access to explore the platform.",
    features: [
      { text: "5 candidate searches", included: true },
      { text: "5 CV requests", included: true },
      { text: "Chat with downloaded students", included: true },
      { text: "Basic candidate profiles", included: true },
      { text: "Persistent search", included: false },
      { text: "Unlimited searches", included: false },
      { text: "Unlimited CV requests", included: false },
      { text: "Priority support", included: false },
    ],
    cta: "Current plan",
    highlighted: false,
    current: true,
  },
  {
    name: "Pro",
    price: "79",
    currency: "\u20AC",
    period: "/year",
    description: "Full access for recruiters actively hiring from university talent.",
    features: [
      { text: "Unlimited candidate searches", included: true },
      { text: "100 CV requests per year", included: true },
      { text: "Chat with downloaded students", included: true },
      { text: "Full candidate profiles", included: true },
      { text: "Search filters & rankings", included: true },
      { text: "Pipeline management", included: true },
      { text: "Up to 3 persistent searches", included: true },
      { text: "Priority support", included: false },
    ],
    cta: "Upgrade to Pro",
    highlighted: true,
    current: false,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    description: "For teams and agencies recruiting at scale across multiple schools.",
    features: [
      { text: "Everything in Pro", included: true },
      { text: "Unlimited persistent searches", included: true },
      { text: "Unlimited CV requests", included: true },
      { text: "Multi-seat access", included: true },
      { text: "Priority support", included: true },
      { text: "Dedicated account manager", included: true },
      { text: "Custom integrations", included: true },
      { text: "API access", included: true },
    ],
    cta: "Contact us",
    highlighted: false,
    current: false,
  },
];

export default function PricingModal({ onClose, currentTier = "free" }) {
  const { session } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const data = await apiPost(
        "/payments/create-checkout-session",
        {},
        session?.access_token
      );
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch {
      setLoading(false);
    }
  };

  const handleEnterprise = () => {
    window.location.href = "mailto:hello@careersorter.co?subject=Enterprise%20plan%20inquiry";
  };

  const tiers = TIERS.map((t) => ({
    ...t,
    current: t.name.toLowerCase() === currentTier,
  }));

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10001,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: COLORS.bg,
          borderRadius: 16,
          maxWidth: 960,
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          padding: "40px 32px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <h2
            style={{
              fontSize: 28,
              fontWeight: 800,
              color: COLORS.text,
              margin: "0 0 8px",
              letterSpacing: "-0.02em",
            }}
          >
            Choose your plan
          </h2>
          <p
            style={{
              fontSize: 16,
              color: COLORS.textSecondary,
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            Get full access to university talent. Upgrade anytime.
          </p>
        </div>

        {/* Tier cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260, 1fr))",
            gap: 20,
            maxWidth: 900,
            margin: "0 auto",
          }}
        >
          {tiers.map((tier) => (
            <div
              key={tier.name}
              style={{
                backgroundColor: COLORS.surface,
                borderRadius: 12,
                padding: "28px 24px",
                border: tier.highlighted
                  ? `2px solid ${COLORS.accent}`
                  : `1px solid ${COLORS.border}`,
                position: "relative",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {tier.highlighted && (
                <div
                  style={{
                    position: "absolute",
                    top: -12,
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: COLORS.accent,
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                    padding: "4px 14px",
                    borderRadius: 20,
                    letterSpacing: "0.02em",
                  }}
                >
                  RECOMMENDED
                </div>
              )}

              {/* Tier name */}
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: COLORS.text,
                  marginBottom: 4,
                }}
              >
                {tier.name}
              </div>

              {/* Price */}
              <div style={{ marginBottom: 12 }}>
                {tier.price === "Custom" ? (
                  <span
                    style={{
                      fontSize: 28,
                      fontWeight: 800,
                      color: COLORS.text,
                    }}
                  >
                    Custom
                  </span>
                ) : (
                  <>
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: COLORS.textSecondary,
                        verticalAlign: "top",
                        position: "relative",
                        top: 6,
                      }}
                    >
                      {tier.currency || ""}
                    </span>
                    <span
                      style={{
                        fontSize: 36,
                        fontWeight: 800,
                        color: COLORS.text,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {tier.price}
                    </span>
                    <span
                      style={{
                        fontSize: 14,
                        color: COLORS.textMuted,
                        marginLeft: 2,
                      }}
                    >
                      {tier.period}
                    </span>
                  </>
                )}
              </div>

              {/* Description */}
              <p
                style={{
                  fontSize: 14,
                  color: COLORS.textSecondary,
                  lineHeight: 1.5,
                  margin: "0 0 20px",
                  minHeight: 42,
                }}
              >
                {tier.description}
              </p>

              {/* Features */}
              <div
                style={{
                  flex: 1,
                  marginBottom: 24,
                }}
              >
                {tier.features.map((f, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 0",
                      fontSize: 13,
                      color: f.included ? COLORS.text : COLORS.textMuted,
                    }}
                  >
                    <span style={{ fontSize: 14, width: 18, textAlign: "center" }}>
                      {f.included ? "\u2713" : "\u2014"}
                    </span>
                    <span style={!f.included ? { textDecoration: "line-through" } : {}}>
                      {f.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* CTA button */}
              {tier.current ? (
                <button
                  disabled
                  style={{
                    width: "100%",
                    padding: "12px 20px",
                    borderRadius: 8,
                    border: `1px solid ${COLORS.border}`,
                    backgroundColor: COLORS.bg,
                    color: COLORS.textMuted,
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: "inherit",
                    cursor: "default",
                  }}
                >
                  Current plan
                </button>
              ) : tier.name === "Pro" ? (
                <button
                  onClick={handleUpgrade}
                  disabled={loading}
                  style={{
                    width: "100%",
                    padding: "12px 20px",
                    borderRadius: 8,
                    border: "none",
                    backgroundColor: loading ? "#9CA3AF" : COLORS.accent,
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 700,
                    fontFamily: "inherit",
                    cursor: loading ? "not-allowed" : "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  {loading ? "Redirecting..." : "Upgrade to Pro"}
                </button>
              ) : (
                <button
                  onClick={handleEnterprise}
                  style={{
                    width: "100%",
                    padding: "12px 20px",
                    borderRadius: 8,
                    border: `1px solid ${COLORS.text}`,
                    backgroundColor: "transparent",
                    color: COLORS.text,
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: "inherit",
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  Contact us
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Close */}
        <div style={{ textAlign: "center", marginTop: 24 }}>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: COLORS.textMuted,
              fontSize: 14,
              cursor: "pointer",
              fontFamily: "inherit",
              textDecoration: "underline",
            }}
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
